import React, { KeyboardEventHandler } from 'react';
import _ from 'lodash';
import { StringsHelper } from "../stringsHelper";
import { Pager } from './pager';
import { Column, IDataTableGroupDictionary, State, IColumn, DataTableExportConfiguration, ColumnSingleValue, ExportConfigurationDefault } from './dataTable.common';
import { HeaderField } from './dataTable.headerField';
import { FooterRow } from './dataTable.footerRow';
import { SelectColumn } from './dataTable.selectColumn';
import { GroupRow } from './dataTable.groupRow';
import { ExcelButton } from './dataTable.excelButton';
import { CsvButton } from './dataTable.csvButton';
import { DataRow } from './dataTable.dataRow';
import { ColumnSelector } from './dataTable.columnSelector';
import { safebind } from '../helpers';

export function GetTable<T>(): {
    new(): Table<T>;
    Column: { new(): Column<T> };
    SelectColumn: { new(): SelectColumn<T> };
} {
    return Table as any;
}

export interface DataTableProps<T> {
    keySelector: (obj: T, index: number) => string;
    data: T[];
    defaultPageSize?: number;
    showHeaders?: boolean;
    filterables?: ((item: T) => any)[];
    storagekey?: string;
    fixedHeader?: boolean,
    fixedHeaderBodyMaxHeight?: number,
    fixedHeaderColumnMinWidth?: number,
    classNameSelector?: (item: T) => string;
    styleSelector?: (item: T | null) => React.CSSProperties | undefined;
    hideSummary?: boolean;
    hideColumnSelector?: boolean;
    style?: React.CSSProperties;
    tableStyle?: React.CSSProperties;
    rowsReadOnly?: boolean;
    additionalHeader?: React.ReactNode;
    onRowClick?: (row: T) => void;
    initialFilterString?: string;
    children: React.ReactNode;
    exportConfiguration?: DataTableExportConfiguration;
    showFilterReset?: boolean;
    onKeyDown?: KeyboardEventHandler<HTMLTableElement>;
}

export const DefaultPointSeparatedExportConfiguration = {
    mode: "csv",
    columnDelimiter: ";",
    includeHeaders: true,
    decimalSeparator: ".",
    quoteText: false,
    exportOnlyVisibleColumns: true
} as DataTableExportConfiguration;
export const DefaultCommaSeparatedExportConfiguration = {
    mode: "csv",
    columnDelimiter: ";",
    includeHeaders: true,
    decimalSeparator: ",",
    quoteText: false,
    exportOnlyVisibleColumns: true
} as DataTableExportConfiguration;

export class Table<T> extends React.Component<DataTableProps<T>, State<T>> {
    static Column = Column;
    static SelectColumn = SelectColumn;

    constructor(props: DataTableProps<T>) {
        super(props);
        this.state = new State<T>(props.storagekey);
        this.state = {
            ...this.state,
            ...this.getNewState(props),
            filterString: props.initialFilterString || this.state.filterString,
        };
    }
    componentWillMount() {
        this.setState({
            ...this.elaborateInternalData(
                this.props.data,
                this.state.filterString,
                this.state.orderType,
                this.state.currentPage,
                this.state.customPageSize,
                Table.getDefaultGroupings(
                    this.state.columns,
                    this.state.groupIndexes
                ),
                this.state.columns,
                this.state.filteredColumns
            )
        });
    }

    private static getDefaultGroupings<T>(columns: IColumn<T>[], groupIndexes: number[]) {
        var result = [...groupIndexes];
        for (var columnN in columns) {
            var column = columns[columnN];
            var groupIndex = column.getDefaultGroupingIndex();
            if (groupIndex !== undefined) {
                var indexOf = result[groupIndex];
                if (indexOf !== undefined) result.splice(groupIndex, 0, +columnN);
                else result.push(+columnN);
            }
        }
        return result;
    }

    private onChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        this.setFilter(value);
    };

    private setFilter = (value: string) => {
        this.setFilterStringToStorageIfNeeded(value);
        this.setState({
            selectedItems: [],
            filterString: value,
            ...this.elaborateInternalData(
                this.props.data,
                value,
                this.state.orderType,
                this.state.currentPage,
                this.state.customPageSize,
                this.state.groupIndexes,
                this.state.columns,
                this.state.filteredColumns
            )
        });
    }

    private onChangeColumnFilter = (column: Column<T>, values: ColumnSingleValue[], checked: boolean) => {

        if (!column || !column.canFilter())
            return;

        const columnIndex = this.state.columns.indexOf(column);
        let filteredColumns = _.clone(this.state.filteredColumns);
        let col = filteredColumns[columnIndex];
        col = col ?? [];

        for (const value of values) {
            const ix = col.indexOf(value);
            if (checked) {
                if (ix >= 0)
                    col.splice(ix, 1);
            } else {
                if (ix < 0)
                    col.push(value);
            }
        }

        filteredColumns[columnIndex] = col;
        this.setState({
            filteredColumns: filteredColumns,
            ...this.elaborateInternalData(
                this.props.data,
                this.state.filterString,
                this.state.orderType,
                this.state.currentPage,
                this.state.customPageSize,
                this.state.groupIndexes,
                this.state.columns,
                filteredColumns
            )
        });
    };

    private onChangeHiddenColumn = (column: Column<T>, hide: boolean) => {

        if (!column)
            return;

        const name = column.props.header && typeof column.props.header === "string" ? column.props.header : undefined;
        const columnIndex = this.state.columns.indexOf(column);

        if (!column.canHide())
            return;

        if (name) {
            let optionalColumns = _.clone(this.state.hiddenColumns);
            let groupIndexes = [...this.state.groupIndexes];

            if (hide) {
                if (!optionalColumns[name] || optionalColumns[name] == true) {
                    optionalColumns[name] = false;
                }
                if (columnIndex >= 0) {
                    const groupIndex = this.state.groupIndexes.indexOf(columnIndex);
                    if (groupIndex >= 0)
                        groupIndexes = this.onRemoveGrouping(columnIndex)
                }
            } else {
                optionalColumns[name] = true;
            }

            this.setHiddenColumnsIfNeeded(optionalColumns);
            this.setState({
                hiddenColumns: optionalColumns,
                ...this.elaborateInternalData(
                    this.props.data,
                    this.state.filterString,
                    this.state.orderType,
                    this.state.currentPage,
                    this.state.customPageSize,
                    groupIndexes,
                    this.state.columns,
                    this.state.filteredColumns
                )
            });
        }
    };

    private setGroupingToStorageIfNeeded(groupIndexes: number[]) {
        if (this.props.storagekey) {
            localStorage.setItem('DataTable' + this.props.storagekey + 'GroupIndexes', JSON.stringify(groupIndexes));
        }
    }
    private setOrderingToStorageIfNeeded(orderType: { ascending: boolean; columnIndex?: number; }) {
        if (this.props.storagekey) {
            localStorage.setItem('DataTable' + this.props.storagekey + 'OrderType', JSON.stringify(orderType));
        }
    }
    private setFilterStringToStorageIfNeeded(value: string) {
        if (this.props.storagekey) {
            localStorage.setItem('DataTable' + this.props.storagekey + 'StringFilter', value);
        }
    }

    private setCurrentPageToStorageIfNeeded(currentPage: number) {
        if (this.props.storagekey) {
            localStorage.setItem('DataTable' + this.props.storagekey + 'CurrentPage', currentPage.toString());
        }
    }

    private setPageSizeToStorageIfNeeded(customPageSize: number) {
        if (this.props.storagekey) {
            localStorage.setItem('DataTable' + this.props.storagekey + 'PageSize', customPageSize.toString());
        }
    }

    private setHiddenColumnsIfNeeded(columnHeaders: { [columnName: string]: boolean }) {
        if (this.props.storagekey) {
            localStorage.setItem('DataTable' + this.props.storagekey + 'OptionalColumns', JSON.stringify(columnHeaders));
        }
    }

    private onChangeOrder = (column: Column<T> | undefined, ascending: boolean) => {
        let orderType = {
            columnIndex: column ? this.state.columns.indexOf(column) : undefined,
            ascending: ascending
        };
        this.setOrderingToStorageIfNeeded(orderType);
        this.setState(
            this.elaborateInternalData(
                this.props.data,
                this.state.filterString,
                orderType,
                this.state.currentPage,
                this.state.customPageSize,
                this.state.groupIndexes,
                this.state.columns,
                this.state.filteredColumns
            )
        );
    };

    private getNewState(nextProps: DataTableProps<T>) {
        const hiddenColumns = this.state.hiddenColumns || [];
        const columns = React.Children
            .toArray(nextProps.children)
            .filter(x => x != null)
            .map(c => {
                var props: any = _.clone((c as any).props) as any;
                props.data = nextProps.data;
                props.keySelector = nextProps.keySelector;
                props.onSetState = safebind(this.forceUpdate, this);

                if (props.hidden && props.header && typeof props.header === "string" && !hiddenColumns[props.header]) {
                    hiddenColumns[props.header] = false;
                }
                return new (c as any).type(props, this.state);
            });
        return {
            selectedItems: this.state.selectedItems || [] as T[],
            hiddenColumns: hiddenColumns,
            columns,
            ...this.elaborateInternalData(
                nextProps.data,
                this.state.filterString,
                this.state.orderType,
                this.state.currentPage,
                this.state.customPageSize,
                this.state.groupIndexes,
                columns,
                this.state.filteredColumns
            )
        };
    }

    componentWillReceiveProps(nextProps: DataTableProps<T>) {
        if (this.props.rowsReadOnly) {
            let comparator =
                (nextProps.keySelector &&
                    ((i1: T, i2: T) =>
                        nextProps.keySelector(i1, 0) === nextProps.keySelector(i2, 1))) ||
                _.isEqual;
            if (
                nextProps.data !== this.props.data &&
                (nextProps.data.length !== this.props.data.length ||
                    _.zip(this.props.data, nextProps.data).some(
                        x => !comparator(x[0]!, x[1]!)
                    ))
            )
                this.setState(this.getNewState(nextProps));
        } else {
            this.setState(this.getNewState(nextProps));
        }
    }

    private static groupByMulti<T>(elements: T[], groups: Column<T>[]): IDataTableGroupDictionary<any> {
        if (!groups || groups.length <= 0) return elements;
        var byFirst = groups[0].group(elements);
        if (groups.length > 1) {
            var restOfGroups = groups.slice(1);
            for (var prop in byFirst) {
                byFirst[prop] = Table.groupByMulti(byFirst[prop], restOfGroups);
            }
        }
        return byFirst;
    }

    private onAddGrouping = (column: Column<T>) => {
        if (
            column && this.state.groupIndexes.find(
                x => x === this.state.columns.indexOf(column)
            ) == undefined
        ) {
            let groupIndexes = [
                ...this.state.groupIndexes,
                this.state.columns.indexOf(column)
            ];
            this.setGroupingToStorageIfNeeded(groupIndexes);
            this.setState(
                this.elaborateInternalData(
                    this.props.data,
                    this.state.filterString,
                    this.state.orderType,
                    this.state.currentPage,
                    this.state.customPageSize,
                    groupIndexes,
                    this.state.columns,
                    this.state.filteredColumns
                )
            );
        }
    };

    private onRemoveGrouping = (columnIndex: number) => {
        let groupIndexes = [...this.state.groupIndexes];
        if (columnIndex != null) {
            _.remove(groupIndexes, x => x === columnIndex);

            this.setGroupingToStorageIfNeeded(groupIndexes);
            this.setState({
                collapsedGroups: [],
                ...this.elaborateInternalData(
                    this.props.data,
                    this.state.filterString,
                    this.state.orderType,
                    this.state.currentPage,
                    this.state.customPageSize,
                    groupIndexes,
                    this.state.columns,
                    this.state.filteredColumns
                )
            });
        }

        return groupIndexes;
    };

    private getPageSize(requested: number | null) {
        return requested != null ? requested : this.props.defaultPageSize || 100;
    }

    private getTotalPages(items: T[], requested: number | null) {
        return Math.max(Math.ceil(items.length / this.getPageSize(requested)), 1);
    }

    private elaborateInternalData(
        array: T[],
        filterString: string,
        orderType: { ascending: boolean; columnIndex?: number },
        cPage: number | null,
        pageSize: number,
        groupIndexes: number[],
        columns: Column<T>[],
        filteredColumns: { [key: number]: ColumnSingleValue[] }
    ) {

        let filteredByColumns = array;
        for (let key in filteredColumns) {
            const values = filteredColumns[key];
            let filterColumn = columns[key];
            filteredByColumns = filterColumn.filter(filteredByColumns, values)
        }

        const filteredData = StringsHelper.filter(
            filteredByColumns,
            filterString,
            this.props.filterables
        );

        let toBeProcessed = filteredData;
        if (orderType.columnIndex != null && orderType.columnIndex != undefined) {
            let orderColumn = columns[orderType.columnIndex];
            if (orderColumn)
                toBeProcessed = orderColumn.order(filteredData, orderType.ascending);
        }

        let currentPage =
            cPage == null
                ? 0
                : Math.min(cPage, this.getTotalPages(toBeProcessed, pageSize) - 1);
        const pSize = this.getPageSize(pageSize);
        let toBeGrouped = _.chain(toBeProcessed)
            .drop(currentPage * pSize)
            .take(pSize)
            .value();
        let internalData = Table.groupByMulti(toBeGrouped, groupIndexes.map(x => columns[x]).filter(x => x != null));
        return {
            itemsDisplayed: toBeGrouped,
            filteredItems: toBeProcessed,
            internalData,
            allItems: Table.getElements(internalData),
            currentPage: currentPage,
            groupIndexes,
            orderType,
        };
    }

    private collapsedItem = (groupLevel: number, key: string, parent: string) => {
        let index = this.state.groupIndexes[groupLevel];
        return this.state.collapsedGroups.find(
            x => x.index === index && x.key === key && x.parent === parent
        );
    };

    private onToggleCollapse = (groupLevel: number, key: string, parent: string) => {
        let index = this.state.groupIndexes[groupLevel];
        const existing = this.collapsedItem(groupLevel, key, parent);
        let collapsedGroups;
        if (existing != null) {
            collapsedGroups = _.without(this.state.collapsedGroups, existing);
        } else {
            collapsedGroups = [
                ...this.state.collapsedGroups,
                { index, key, parent }
            ];
        }
        this.setState({ collapsedGroups });
    };

    private getRows(
        index: { count: number },
        groupLevel: { readonly count: number },
        group: string | null,
        key: string,
        item: T[] | IDataTableGroupDictionary<T[]>
    ) {
        var result: React.ReactElement<any>[] = [];
        if (group) {
            groupLevel = { count: groupLevel.count + 1 };
            const column = this.state.columns[this.state.groupIndexes[groupLevel.count]];
            result.push(
                <GroupRow
                    onRemoveGrouping={safebind(
                        this.onRemoveGrouping,
                        this,
                        this.state.groupIndexes[groupLevel.count]
                    )}
                    key={'group' + index.count}
                    columns={this.state.columns}
                    groupKey={key}
                    groupLevel={groupLevel.count}
                    column={column}
                    elements={Table.getElements(item)}
                    isCollapsed={
                        this.collapsedItem(groupLevel.count, key, group) != null
                    }
                    onToggleCollapse={safebind(
                        this.onToggleCollapse,
                        this,
                        groupLevel.count,
                        key,
                        group!
                    )}
                />
            );
        }


        if (group && this.collapsedItem(groupLevel.count, key, group) != null) {
            if (Array.isArray(item)) {
                result.push(
                    <FooterRow
                        key={'f' + index.count}
                        columns={this.visibleColumns()}
                        elements={item}
                        allElements={Table.getElements(item)}
                        colSpan={this.visibleColumns().length}
                        groupLevel={groupLevel.count}
                    />
                );
            }
            groupLevel = { count: groupLevel.count - 1 };
            return result;
        }

        if (Array.isArray(item)) {
            item.forEach(
                (element, index) => {
                    result.push(
                        <DataRow
                            key={((this.props.keySelector || JSON.stringify) as Function)(
                                element,
                                index
                            )}
                            classNameSelector={this.props.classNameSelector}
                            styleSelector={this.props.styleSelector}
                            datarow={element}
                            columns={this.visibleColumns()}
                            onRowClick={this.props.onRowClick}
                        />
                    )
                }
                ,
                this
            );
            result.push(
                <FooterRow
                    key={'f' + index.count}
                    columns={this.visibleColumns()}
                    elements={item}
                    allElements={Table.getElements(item)}
                    colSpan={this.visibleColumns().length}
                    groupLevel={groupLevel.count}
                />
            );
            groupLevel = { count: groupLevel.count - 1 };
            return result;
        } else {
            for (var k2 in item) {
                index.count++;
                this.getRows(index, groupLevel, key, k2, (item as any)[k2]).forEach(x => result.push(x));
            }
            groupLevel = { count: -1 };
            return result;
        }
    }

    private static getElements<T>(grouped: IDataTableGroupDictionary<T[]> | T[]): T[] {
        if (Array.isArray(grouped)) {
            return grouped;
        } else {
            var result: T[] = [];
            for (var key in grouped) {
                this.getElements(grouped[key]).forEach(x => result.push(x));
            }
            return result;
        }
    }

    private onChangePage = (page: number) => {
        this.setCurrentPageToStorageIfNeeded(page);
        this.setState(
            this.elaborateInternalData(
                this.props.data,
                this.state.filterString,
                this.state.orderType,
                page,
                this.state.customPageSize,
                this.state.groupIndexes,
                this.state.columns,
                this.state.filteredColumns
            )
        );
    };

    private onChangePageSize = (e: React.MouseEvent<HTMLAnchorElement>) => {
        let actualPageSize = this.getPageSize(this.state.customPageSize);
        let totElements = (this.state.allItems || []).length;
        var defaultValue = totElements < actualPageSize ? totElements : actualPageSize;
        let promptResult = prompt('Modifica numero elementi per pagina', defaultValue.toString());
        if (promptResult != null) {
            let newPageSize = parseInt(promptResult);
            if (!isNaN(newPageSize)) {
                this.setCurrentPageToStorageIfNeeded(0);
                this.setPageSizeToStorageIfNeeded(newPageSize);
                this.setState({
                    ...this.elaborateInternalData(
                        this.props.data,
                        this.state.filterString,
                        this.state.orderType,
                        0,
                        newPageSize,
                        this.state.groupIndexes,
                        this.state.columns,
                        this.state.filteredColumns
                    ),
                    customPageSize: newPageSize
                });
            }
        }
    };

    shouldComponentUpdate(nextProps: DataTableProps<T>, nextState: State<T>) {
        if (
            this.props.rowsReadOnly === true &&
            nextState.internalData === this.state.internalData
        ) {
            return false;
        }
        return true;
    }

    private isHiddenColumn = (column: Column<T>) => {
        const hiddenCols = this.state.hiddenColumns || [];
        if (column.canHide() && column.props.header && typeof column.props.header === "string") {
            const showed = hiddenCols[column.props.header];
            return showed != null && !showed;
        }

        return false;
    }

    private visibleColumns = () => {
        return this.state.columns.filter(x => !this.isHiddenColumn(x));
    }

    private isFilteringEnabled = () => {
        return this.state.columns.findIndex(x => x.canFilter()) >= 0;
    }

    private getHeaderRow = () => {
        var ths: JSX.Element[] = [];
        if ((this.state.columns.length > 0 && this.props.showHeaders) || true) {
            const columns = this.visibleColumns();
            for (var columnN in columns) {
                var column = columns[columnN];
                const columnIndex = this.state.columns.indexOf(column);
                ths.push(
                    <HeaderField
                        key={`header_${columnN}`}
                        columnKey={columnN}
                        column={column}
                        onAddGrouping={this.onAddGrouping}
                        onRemoveGrouping={safebind(
                            this.onRemoveGrouping,
                            this,
                            this.state.columns.indexOf(column)
                        )}
                        onChangeOrder={this.onChangeOrder}
                        orderStatus={{
                            ascending: this.state.orderType.ascending,
                            column: this.state.orderType.columnIndex != null
                                ? this.state.columns[this.state.orderType.columnIndex]
                                : undefined
                        }}
                        groupsStatus={this.state.groupIndexes.map(
                            x => this.state.columns[x]
                        )}
                        visibleElements={this.state.allItems}
                        allElements={this.props.data}
                        fixedHeaderColumnMinWidth={this.props.fixedHeaderColumnMinWidth}
                        onChangeFilter={this.onChangeColumnFilter}
                        filteredValues={this.state.filteredColumns[columnIndex] ?? []}
                    />
                );
            }
        }

        return (<tr style={this.props.styleSelector && this.props.styleSelector(null)}>
            {ths}
        </tr>);
    }

    private getTableBody = () => {

        if (this.props.data.length > 0 && this.state.itemsDisplayed.length === 0) {
            const columns = this.visibleColumns().length > 0 ? this.visibleColumns().length : 1;
            return (<tr>
                <td colSpan={columns}>
                    <div className="alert alert-warning text-center" style={this.props.style}>Nessun risultato trovato</div>
                </td>
            </tr>)
        } else {
            return (<>
                {this.getRows({ count: 0 }, { count: -1 }, null, 'first', this.state.internalData)}
                {this.state.groupIndexes != undefined && this.state.groupIndexes!.length > 0
                    ? (<FooterRow
                        key="footer"
                        columns={this.visibleColumns()}
                        elements={this.state.filteredItems}
                        allElements={this.state.allItems}
                        colSpan={this.visibleColumns().length}
                        groupLevel={0}
                    />) : null}
            </>);
        }

    }

    private getTableElement = () => {

        let tablestyle: String = this.props.fixedHeader ? "table-fixed-header" : "";

        let bodystyle: React.CSSProperties = this.props.fixedHeader ? {
            maxHeight: this.props.fixedHeaderBodyMaxHeight ? this.props.fixedHeaderBodyMaxHeight : 350
        } : {};

        const style: React.CSSProperties | undefined = this.isFilteringEnabled() ? { ...this.props.style, overflowX: "visible" } : this.props.style;

        return (
            <div onKeyDown={this.props.onKeyDown} tabIndex={this.props.onKeyDown != null ? 1 : undefined} className="table-responsive" style={style}>
                <table className={"table table-striped table-hover data-table " + tablestyle} style={this.props.tableStyle} ref={x => (this.tableElement = x!)}>
                    <thead>
                        {this.getHeaderRow()}
                    </thead>
                    <tbody style={bodystyle}>
                        {this.getTableBody()}
                    </tbody>
                </table>
            </div>
        );

    };

    private getExportButton = (style?: React.CSSProperties | undefined) => {
        const exportConfig = { ...ExportConfigurationDefault, ...this.props.exportConfiguration };

        if (exportConfig.mode == "none")
            return null;

        if (exportConfig.mode == "csv") {
            const cols = exportConfig.exportOnlyVisibleColumns ? this.visibleColumns() : this.state.columns;
            return <CsvButton storagekey={this.props.storagekey} style={style} data={this.state.filteredItems} columns={cols} config={exportConfig} />
        } else {
            return <ExcelButton storagekey={this.props.storagekey} tableElement={() => this.tableElement} style={style} />
        }
    }

    private getSummaryForColumn(column: Column<T>, index: number, items: T[]) {
        const summaryItem = column.getSummaryItem(items);
        if (summaryItem)
            return (<span key={index}>{summaryItem}{' '}</span>)

        return null
    }


    private onResetClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        this.setFilter("");
        this.searchInput.current?.focus();
    }

    private onPrint = () => {
        const oldPageSize = this.state.customPageSize || 100;
        this.setPageSize(this.props.data.length, () => {
            window.print();
            this.setPageSize(oldPageSize);
        });
    }

    private setPageSize(newPageSize: number, onReady?: () => void) {
        this.setCurrentPageToStorageIfNeeded(0);
        this.setPageSizeToStorageIfNeeded(newPageSize);
        this.setState({
            ...this.elaborateInternalData(
                this.props.data,
                this.state.filterString,
                this.state.orderType,
                0,
                newPageSize,
                this.state.groupIndexes,
                this.state.columns,
                this.state.filteredColumns
            ),
            customPageSize: newPageSize
        }, () => {
            if (onReady) onReady();
        });
    }

    private tableElement: HTMLTableElement;
    private searchInput = React.createRef<HTMLInputElement>();

    render() {
        let table = this.getTableElement();
        const className = this.props.filterables ? 'col-12 col-sm-12 col-lg-4 col-xl-5' : 'col-12';
        const hiddenColumns = Object.keys(this.state.hiddenColumns).filter(k => this.state.hiddenColumns[k] === false);
        return (
            <div>
                <div className="row d-print-none">
                    {this.props.filterables
                        ? <div className="col-12 col-sm-12 col-lg-8 col-xl-7">
                            <span className="input-group">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Cerca nella tabella..."
                                    onChange={this.onChangeFilter}
                                    value={this.state.filterString}
                                    ref={this.searchInput}
                                />
                                <span className="input-group-text">
                                    <i className="fa fa-search" />
                                </span>
                                <span className="input-group-text">
                                    {this.getExportButton()}
                                </span>
                                <a href="#" className="input-group-text" onClick={this.onPrint}>
                                    <i className="fas fa-print" />
                                </a>
                                {(this.props.showFilterReset === true) && <span className="input-group-text">
                                    <a
                                        href="#"
                                        className="text-dark fw-bold"
                                        title="reset"
                                        onClick={this.onResetClick}
                                    >
                                        RESET
                                    </a>
                                </span>
                                }
                            </span>
                        </div>
                        : <div />}
                    {this.props.hideSummary ?
                        <div className={className}>
                            {this.props.additionalHeader || null}
                        </div>
                        : <div className={className}>
                            <div className="d-flex align-items-center justify-content-end me-2" style={{ margin: '5px 10px 0 0' }}>
                                {this.props.filterables
                                    ? null
                                    : this.getExportButton({ marginRight: 5 })
                                }
                                <i className="fa fa-table me-1" />
                                {this.state.allItems.length !== this.state.filteredItems.length
                                    ? <span className="me-2">
                                        <a className="link-primary fw-bold" title="Modifica elementi per pagina"
                                            onClick={this.onChangePageSize}>
                                            {this.state.allItems.length}
                                        </a>/{this.state.filteredItems.length} Tot.
                                    </span>
                                    : <span className="me-2">Tot:{' '}<a title="Modifica elementi per pagina" onClick={this.onChangePageSize}>{this.state.allItems.length}</a></span>}
                                {this.state.columns.map((c, i) => this.getSummaryForColumn(c, i, this.state.allItems))}
                                {this.props.hideColumnSelector ? null : (<ColumnSelector
                                    columns={this.state.columns}
                                    hiddenCols={hiddenColumns}
                                    onChangeSelection={this.onChangeHiddenColumn}
                                />)}
                                {this.props.additionalHeader && <div className={className}>
                                    {this.props.additionalHeader}
                                </div>}
                                <Pager
                                    currentPage={this.state.currentPage}
                                    totalPages={this.getTotalPages(
                                        this.state.filteredItems,
                                        this.state.customPageSize
                                    )}
                                    onChangePage={this.onChangePage}
                                />
                            </div>
                        </div>
                    }
                    <div className="col-12">
                        {this.visibleColumns().map((c, i) => (<span key={i}>{c.getHeadRow()}</span>))}
                    </div>
                </div>
                <div style={{ marginTop: 5 }}>{table}</div>
                <div className="float-end d-print-none">
                    <Pager
                        currentPage={this.state.currentPage}
                        totalPages={this.getTotalPages(
                            this.state.filteredItems,
                            this.state.customPageSize
                        )}
                        onChangePage={this.onChangePage}
                    />
                </div>
                <div className="clearfix" />
            </div>
        );
    }
}